import { useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import posthog from "posthog-js";

import { CarePlanStepStatus } from "@/components/care-plan/types";
import { useProgressNavigation } from "@/components/progress-bar";

import { queryClient } from "@/config/react-query";

import { useUser } from "@/contexts/user-provider";

import { updatePatientStatusCacheEntries } from "@/hooks/use-checklist-realtime-cache";

import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { toMs } from "@/lib/timestamp";
import { getTimezone } from "@/lib/timezone";
import { toast } from "@/lib/use-toast";

import { BookableUsageRecord } from "@/types/bookable";
import type { CarePlanDetail } from "@/types/care-plan";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { BREAK_BLOCK_TYPES, EventStatus, EventType } from "@/types/enums/event";
import { EventIcon } from "@/types/enums/event";
import {
  EventCreateStatus,
  GoogleCalendarSyncOwner,
} from "@/types/enums/google-calendar-sync";
import { MessageType } from "@/types/enums/message";
import { RecurrenceModificationMode } from "@/types/enums/recurrence-modification-mode";
import { UserType } from "@/types/enums/user-type";
import type { AppointmentChecklistItem } from "@/types/event";
import {
  EventCreate,
  EventCreateResponse,
  EventDeleteResponse,
  EventDetail,
  EventStatusUpdateResponse,
  EventTiny,
  EventUpdate,
  EventUpdateResponse,
  GoogleCalendarSyncFailure,
  PatientStatusEventResponse,
  ShiftsByDateResponse,
  isBookableBlockEvent,
} from "@/types/event";
import type { ShiftBreakPlan } from "@/types/location";
import type { MessageSummary } from "@/types/message";
import type { PatientSidebarCounts } from "@/types/patient-sidebar";
import type { UserTiny } from "@/types/user";

import { eventLogQueryOptions } from "./event-log-query";
import {
  CalendarLocationFilterRequest,
  canonicalCalendarLocationFilter,
  serializeCalendarGetParams,
} from "./location-filter-contract";
import { requestData } from "@/api/api-config";
import { todaysItemsQueryKeys } from "@/api/provider/billing/todays-items-query";
import { carePlanQueryKeys } from "@/api/provider/patient/care-plan-query";
import {
  optimisticAdjustSidebarCounts,
  revertSidebarCounts,
} from "@/api/provider/user/patient/sidebar-cache-utils";
import {
  invalidateByKey,
  revertOnError,
  startOptimisticChange,
} from "@/api/utils/query-helpers";
import { useTenantStore } from "@/stores/tenant-store";

async function listEvents(
  start?: Date,
  end?: Date,
  userIds?: string[],
  locationFilter: CalendarLocationFilterRequest = {},
  eventTypes?: EventType[],
): Promise<EventTiny[] | null> {
  return await requestData<EventTiny[]>("check-in/calendar/event/list", "get", {
    searchParams: serializeCalendarGetParams(
      {
        start: start?.toISOString(),
        end: end?.toISOString(),
        userIds: userIds ? [...userIds].sort() : undefined,
        types: eventTypes ? [...eventTypes].sort() : undefined,
      },
      locationFilter,
    ),
    timeout: 90000,
  });
}

/**
 * Fetches events for specified locations and providers within a date range.
 * Event types are handled client-side for provider ranges. An empty provider
 * list represents the provider-free bookable-block lane and fetches only BLOCK
 * events before filtering to true bookable blocks.
 *
 * Errors are NOT swallowed (catchError=false): the caller's queryFn must see
 * failures so React Query keeps previously-cached data instead of committing
 * an empty result as fresh.
 */
async function listEventsByLocationAndProvider(
  locationIds: string[],
  providerIds: string[],
  locationFilter: CalendarLocationFilterRequest,
  start?: Date,
  end?: Date,
  signal?: AbortSignal,
): Promise<EventTiny[] | null> {
  // Calendar lanes require a concrete selected location. The generic event-list
  // endpoint still supports the contract's unassigned-only truth-table row.
  if (locationIds.length === 0) return [];

  const isBookableOnlyRange = providerIds.length === 0;
  const events = await requestData<EventTiny[]>(
    "check-in/calendar/event/list",
    "get",
    {
      searchParams: serializeCalendarGetParams(
        {
          start: start?.toISOString(),
          end: end?.toISOString(),
          userIds: isBookableOnlyRange ? undefined : providerIds,
          types: isBookableOnlyRange ? [EventType.BLOCK] : undefined,
        },
        locationFilter,
      ),
      signal,
      timeout: 90000,
    },
    /* catchError */ false,
  );

  return isBookableOnlyRange
    ? (events?.filter(isBookableBlockEvent) ?? [])
    : events;
}

async function searchEvents(
  searchTerm: string,
  controller?: AbortController | null,
): Promise<EventTiny[] | null> {
  return await requestData<EventTiny[]>("check-in/calendar/event/list", "get", {
    searchParams: { search: searchTerm },
    signal: controller?.signal,
    timeout: 90000,
  });
}

async function getConflictedShiftIds(
  start: Date,
  end: Date,
): Promise<EventTiny[] | null> {
  return await requestData<EventTiny[]>(
    "check-in/calendar/event/shift/conflicts",
    "get",
    {
      searchParams: { start: start.toISOString(), end: end.toISOString() },
    },
  );
}

async function searchPatientEvents(
  patientId: string,
  searchTerm: string,
  controller: AbortController | null = null,
  statuses?: EventStatus[],
): Promise<EventDetail[] | null> {
  return await requestData<EventDetail[]>(
    `check-in/calendar/events-for-patient/${patientId}`,
    "get",
    {
      searchParams: { searchTerm, statuses },
      signal: controller?.signal,
    },
  );
}

async function getPatientEventsInRange(
  patientId: string,
  start: Date,
  end: Date,
): Promise<EventDetail[] | null> {
  return await requestData<EventDetail[]>(
    `check-in/calendar/events-for-patient/${patientId}`,
    "get",
    {
      searchParams: { start: start.toISOString(), end: end.toISOString() },
    },
  );
}

async function getAppointmentChecklist(
  eventId: string,
): Promise<AppointmentChecklistItem[] | null> {
  return await requestData<AppointmentChecklistItem[]>(
    "check-in/calendar/appointment-checklist",
    "get",
    { searchParams: { eventId } },
  );
}

async function getPatientStatus(
  locationFilter: CalendarLocationFilterRequest,
  timezone: string,
): Promise<PatientStatusEventResponse[] | null> {
  return await requestData<PatientStatusEventResponse[]>(
    "check-in/calendar/patient-status",
    "get",
    {
      searchParams: serializeCalendarGetParams({ timezone }, locationFilter),
    },
  );
}

async function completeAppointmentChecklist(
  appointmentChecklistId: string,
  eventId: string,
  completedDate?: Date,
): Promise<boolean | null> {
  return await requestData<boolean>(
    "check-in/calendar/appointment-checklist/update",
    "post",
    {
      json: {
        appointmentChecklistId,
        eventId,
        completedDate: completedDate?.toISOString(),
      },
    },
  );
}

async function createEvent(
  event: EventCreate,
  timezone: string,
  sendNotifications: boolean = true,
  sendSmsConfirmation?: boolean,
): Promise<EventCreateResponse | null> {
  const searchParams: Record<string, string | boolean> = {
    timezone,
    sendNotifications,
  };
  if (sendSmsConfirmation !== undefined) {
    searchParams.sendSmsConfirmation = sendSmsConfirmation;
  }
  return await requestData<EventCreateResponse>(
    "check-in/calendar/create",
    "post",
    {
      json: event,
      searchParams,
      timeout: 60_000,
    },
    false,
  );
}

/**
 * Shifts each segment's start/end by the same delta as the event's top-level
 * start. Used by the optimistic-patch path so the cache stays temporally
 * consistent if the post-mutation refetch is delayed or fails.
 */
export function shiftSegmentsByEventStartDelta<
  S extends { start: string; end: string },
>(
  segments: readonly S[] | undefined | null,
  prevStart: string | undefined | null,
  newStart: Date | undefined | null,
): readonly S[] | undefined | null {
  if (!segments?.length || !prevStart || !newStart) return segments;
  const deltaMs = newStart.getTime() - toMs(prevStart);
  if (deltaMs === 0) return segments;
  return segments.map((seg) => ({
    ...seg,
    start: new Date(toMs(seg.start) + deltaMs).toISOString(),
    end: new Date(toMs(seg.end) + deltaMs).toISOString(),
  }));
}

async function getEventDetail(eventId: string): Promise<EventDetail | null> {
  const timezone = getTimezone();
  return await requestData<EventDetail>(
    `check-in/calendar/detail/${eventId}?timezone=${timezone}`,
    "get",
  );
}

async function deleteEvent(
  eventId: string,
  recurrenceModificationMode?: RecurrenceModificationMode,
  cancellationReason?: string,
  sendSms: boolean = true,
  cancellationMessage?: string,
): Promise<EventDeleteResponse | null> {
  const searchParams = {
    recurrenceModificationMode,
    cancellationReason,
    send_sms: sendSms,
    cancellation_message: cancellationMessage,
  };

  return await requestData<EventDeleteResponse | null>(
    `check-in/calendar/delete/${eventId}`,
    "delete",
    {
      searchParams,
    },
    false,
  );
}

async function updateEvent(
  event: EventUpdate,
): Promise<EventUpdateResponse | null> {
  const tz = getTimezone();
  return await requestData<EventUpdateResponse>(
    "check-in/calendar/update",
    "put",
    {
      json: {
        ...event,
        tz,
      },
    },
    false,
  );
}

/**
 * Deletes a recurring event
 * @param eventId The ID of an event in the recurring series
 * @param recurrenceModificationMode How to handle the deletion (FUTURE or ALL)
 * @returns List of deleted event IDs
 */
async function deleteRecurringEvent(
  eventId: string,
  recurrenceModificationMode: RecurrenceModificationMode,
): Promise<EventDeleteResponse | null> {
  return await requestData<EventDeleteResponse>(
    `check-in/calendar/recurring-event/${eventId}`,
    "delete",
    {
      searchParams: { recurrenceModificationMode },
    },
    false,
  );
}

async function updateEventStatus(
  eventId: string,
  status: EventStatus,
  cancellationReason?: string,
  sendSms: boolean = true,
  cancellationMessage?: string,
  recurrenceModificationMode?: RecurrenceModificationMode,
  chargeCancellationFee: boolean = true,
  chargeNoShowFee: boolean = true,
): Promise<EventStatusUpdateResponse | null> {
  return await requestData<EventStatusUpdateResponse>(
    `check-in/calendar/update-status/${eventId}`,
    "put",
    {
      json: {
        status,
        cancellationReason,
        sendSms,
        cancellationMessage,
        recurrenceModificationMode,
        chargeCancellationFee,
        chargeNoShowFee,
      },
    },
  );
}

async function resendEventConfirmation(
  eventId: string,
): Promise<{ sent: boolean } | null> {
  return await requestData<{ sent: boolean }>(
    `check-in/calendar/event/${eventId}/resend-confirmation`,
    "post",
    undefined,
    false,
  );
}

async function generateGoogleMeetLink(
  eventId: string,
): Promise<EventDetail | null> {
  return await requestData<EventDetail>(
    `check-in/calendar/event/${eventId}/google-meet-link`,
    "post",
    undefined,
    false,
  );
}

async function authorizeGoogleCalendar(): Promise<string | null> {
  return await requestData<string>("check-in/calendar/gcal/authorize", "get");
}

async function authorizeTenantGoogleCalendar(): Promise<string | null> {
  return await requestData<string>(
    "check-in/calendar/gcal/tenant/authorize",
    "get",
  );
}

/**
 * Gets all shifts for a specific date with provider information
 * @param date Date to get shifts for
 * @returns Shifts by date response
 */
async function getShiftsByDate(
  date: Date,
  timeZone?: string,
): Promise<ShiftsByDateResponse | null> {
  return await requestData<ShiftsByDateResponse>(
    "check-in/calendar/shifts/by-date",
    "get",
    {
      searchParams: {
        date: format(date, "yyyy-MM-dd"), // Send as YYYY-MM-DD format
        ...(timeZone ? { timezone: timeZone } : {}),
      },
    },
  );
}

async function getShiftBreakPlan(
  locationId: string,
  start: Date,
  end: Date,
): Promise<ShiftBreakPlan | null> {
  return await requestData<ShiftBreakPlan>(
    "check-in/calendar/event/shift/break-plan",
    "get",
    {
      searchParams: {
        locationId,
        start: start.toISOString(),
        end: end.toISOString(),
      },
    },
  );
}

/**
 * Break and lunch blocks for one staff member that overlap a shift window: those at
 * the shift's location plus those set to all locations (no location).
 */
async function listShiftBreaks(
  providerId: string,
  locationId: string,
  start: Date,
  end: Date,
): Promise<EventTiny[]> {
  const blocks = await listEvents(
    start,
    end,
    [providerId],
    { locationIds: [locationId], includeUnassigned: true },
    [EventType.BLOCK],
  );
  return (blocks ?? []).filter(
    (block) =>
      block.blockType != null &&
      BREAK_BLOCK_TYPES.includes(block.blockType) &&
      block.status !== EventStatus.CANCELLED &&
      toMs(block.start) < end.getTime() &&
      toMs(block.end) > start.getTime(),
  );
}

async function listBookableUsage(
  bookableIds: string[],
  start?: Date,
  end?: Date,
  locationId?: string,
): Promise<BookableUsageRecord[] | null> {
  return await requestData<BookableUsageRecord[]>(
    "check-in/calendar/bookable/usage",
    "get",
    {
      searchParams: {
        bookableIds,
        start: start?.toISOString(),
        end: end?.toISOString(),
        locationId,
      },
      timeout: 60000,
    },
  );
}

interface DeleteShiftsResponse {
  count: number;
  eventIds: string[];
  removedBreakEventIds: string[];
}

/** Deletes shifts for specific providers on a clinic-local date. */
async function deleteShiftsByProvider(
  date: Date,
  providerIds: string[],
): Promise<DeleteShiftsResponse | null> {
  return await requestData<DeleteShiftsResponse>(
    "check-in/calendar/shifts/delete-by-provider",
    "delete",
    {
      json: {
        date: format(date, "yyyy-MM-dd"),
        provider_ids: providerIds,
        return_event_ids: true,
      },
    },
    false,
  );
}

/** Deletes all shifts for the specified clinic-local dates. */
async function deleteShiftsByDates(
  dates: Date[],
): Promise<DeleteShiftsResponse | null> {
  return await requestData<DeleteShiftsResponse>(
    "check-in/calendar/shifts/delete-by-dates",
    "delete",
    {
      json: {
        dates: dates.map((date) => format(date, "yyyy-MM-dd")),
        return_event_ids: true,
      },
    },
    false,
  );
}

async function getEventMessages(
  eventId: string,
): Promise<MessageSummary[] | null> {
  return await requestData<MessageSummary[]>(
    `check-in/calendar/event/messages/${eventId}`,
    "get",
  );
}

async function getEventMessageTypesBatch(
  eventIds: string[],
): Promise<Record<string, MessageType[]> | null> {
  if (eventIds.length === 0) return null;
  return await requestData<Record<string, MessageType[]>>(
    "check-in/calendar/event/messages/types",
    "get",
    {
      searchParams: { eventIds },
    },
  );
}

async function getEventMessageTypes(eventId: string): Promise<MessageType[]> {
  const result = await getEventMessageTypesBatch([eventId]);
  return result?.[eventId] ?? [];
}

export const eventQueryKeys = {
  all: ["calendar", "event"] as const,
  lists: () => [...eventQueryKeys.all, "list"] as const,
  tinies: () => [...eventQueryKeys.all, "tiny"] as const,
  tiny: (eventId: string) => [...eventQueryKeys.tinies(), eventId] as const,
  list: (
    start?: Date,
    end?: Date,
    userIds?: string[],
    locationFilter: CalendarLocationFilterRequest = {},
    eventTypes?: EventType[],
  ) =>
    [
      ...eventQueryKeys.lists(),
      {
        start: start?.toISOString(),
        end: end?.toISOString(),
        userIds: userIds ? [...userIds].sort() : undefined,
        ...canonicalCalendarLocationFilter(locationFilter),
        eventTypes: eventTypes ? [...eventTypes].sort() : undefined,
      },
    ] as const,
  allPatientResults: () =>
    [...eventQueryKeys.all, "searchPatientEvents"] as const,
  searchPatientEvents: (patientId: string, searchTerm: string) =>
    [...eventQueryKeys.allPatientResults(), patientId, searchTerm] as const,
  patientEventsInRange: (patientId: string, start: Date, end: Date) =>
    [
      ...eventQueryKeys.allPatientResults(),
      patientId,
      { start: start.toISOString(), end: end.toISOString() },
    ] as const,
  search: (term: string) => [...eventQueryKeys.all, "search", term] as const,
  appointmentChecklist: (eventId: string) =>
    [...eventQueryKeys.all, "appointmentChecklist", eventId] as const,
  eventMessages: (eventId: string) =>
    [...eventQueryKeys.all, "messages", eventId] as const,
  messageTypes: (eventId: string) =>
    [...eventQueryKeys.all, "messageTypes", eventId] as const,
  messageTypesBatch: (eventIds: string[]) =>
    [
      ...eventQueryKeys.all,
      "messageTypesBatch",
      { ids: [...eventIds].sort() },
    ] as const,
  details: () => [...eventQueryKeys.all, "detail"] as const,
  detail: (eventId: string) => [...eventQueryKeys.details(), eventId] as const,
  updateStatus: (eventId: string, status: EventStatus) =>
    [...eventQueryKeys.all, "updateStatus", eventId, status] as const,
  authorizeGoogleCalendar: () =>
    [...eventQueryKeys.all, "authorizeGoogleCalendar"] as const,
  authorizeTenantGoogleCalendar: () =>
    [...eventQueryKeys.all, "authorizeTenantGoogleCalendar"] as const,
  completeAppointment: (eventId: string) =>
    [...eventQueryKeys.all, "completeAppointment", eventId] as const,
  allConflictedShifts: () =>
    [...eventQueryKeys.all, "getConflictedShifts"] as const,
  conflictedShiftIds: (start: Date, end: Date) =>
    [
      ...eventQueryKeys.allConflictedShifts(),
      start.toISOString(),
      end.toISOString(),
    ] as const,
  shifts: () => [...eventQueryKeys.all, "shifts"] as const,
  shiftBreakPlan: (locationId: string | null, start: Date, end: Date) =>
    [
      ...eventQueryKeys.shifts(),
      "break-plan",
      locationId,
      start.toISOString(),
      end.toISOString(),
    ] as const,
  shiftBreaks: (
    providerId: string | null,
    locationId: string | null,
    start: Date | null,
    end: Date | null,
  ) =>
    [
      ...eventQueryKeys.shifts(),
      "breaks",
      providerId,
      locationId,
      start?.toISOString() ?? null,
      end?.toISOString() ?? null,
    ] as const,
  shiftsByDate: (date: Date | null, timeZone?: string) =>
    [
      ...eventQueryKeys.shifts(),
      "by-date",
      date ? format(date, "yyyy-MM-dd") : "null", // Match the clinic-local request date.
      timeZone ?? null,
    ] as const,
  // Per-location caching for incremental fetching
  byLocations: () => [...eventQueryKeys.all, "by-location"] as const,
  byLocation: (
    locationId: string,
    start?: Date,
    end?: Date,
    includeUnassigned: boolean = true,
  ) =>
    [
      ...eventQueryKeys.byLocations(),
      locationId,
      {
        start: start?.toISOString(),
        end: end?.toISOString(),
        includeUnassigned,
      },
    ] as const,
  // Bookable usage
  bookableUsage: (
    bookableIds: string[],
    start?: Date,
    end?: Date,
    locationId?: string,
  ) =>
    [
      ...eventQueryKeys.all,
      "bookableUsage",
      {
        bookableIds: [...bookableIds].sort(),
        start: start?.toISOString(),
        end: end?.toISOString(),
        locationId,
      },
    ] as const,
  // The location filter stays last: realtime cache seeding reads it from there.
  patientStatus: (
    locationFilter: CalendarLocationFilterRequest,
    timezone: string,
  ) =>
    [
      ...eventQueryKeys.all,
      "patientStatus",
      timezone,
      canonicalCalendarLocationFilter(locationFilter),
    ] as const,
  // Search patient events with optional statuses filter
  searchPatientEventsWithStatuses: (
    patientId: string,
    searchTerm: string,
    statuses?: EventStatus[],
  ) =>
    [
      ...eventQueryKeys.allPatientResults(),
      patientId,
      searchTerm,
      { statuses: statuses?.sort() },
    ] as const,
};

export const eventQueryOptions = {
  tiny: (eventId: string) => ({
    queryKey: eventQueryKeys.tiny(eventId),
    // Disabled: this cache is populated via setQueryData only
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    // No-op queryFn (won't run because enabled: false)
    queryFn: () => Promise.resolve(null),
  }),
  list: (
    start?: Date,
    end?: Date,
    userIds?: string[],
    locationIds?: string[],
    eventTypes?: EventType[],
    enabled: boolean = true,
    includeUnassigned: boolean = (locationIds?.length ?? 0) > 0,
  ) => {
    const locationFilter = canonicalCalendarLocationFilter({
      locationIds,
      includeUnassigned,
    });
    return {
      queryKey: eventQueryKeys.list(
        start,
        end,
        userIds,
        locationFilter,
        eventTypes,
      ),
      queryFn: () =>
        listEvents(start, end, userIds, locationFilter, eventTypes),
      enabled,
      refetchOnMount: false,
      // Refetch when window regains focus (handles tab becoming active)
      refetchOnWindowFocus: true,
      // Refetch when network reconnects
      refetchOnReconnect: true,
      // Consider data stale after 30 seconds to trigger refetch on focus
      staleTime: 1000 * 30,
      // Keep data in cache for 5 minutes
      gcTime: 1000 * 60 * 5,
    };
  },
  searchPatientEvents: (
    patientId: string,
    searchTerm: string,
    statuses?: EventStatus[],
  ) => ({
    queryKey: statuses
      ? eventQueryKeys.searchPatientEventsWithStatuses(
          patientId,
          searchTerm,
          statuses,
        )
      : eventQueryKeys.searchPatientEvents(patientId, searchTerm),
    queryFn: () => searchPatientEvents(patientId, searchTerm, null, statuses),
  }),
  patientEventsInRange: (patientId: string, start: Date, end: Date) => ({
    queryKey: eventQueryKeys.patientEventsInRange(patientId, start, end),
    queryFn: () => getPatientEventsInRange(patientId, start, end),
  }),
  search: (term: string, controller?: AbortController | null) => ({
    queryKey: eventQueryKeys.search(term),
    queryFn: () => searchEvents(term, controller ?? undefined),
    select: (data: EventTiny[] | null) => data ?? [],
  }),
  patientStatus: (
    locationFilter: CalendarLocationFilterRequest,
    timezone: string,
    enabled: boolean = true,
  ) => ({
    queryKey: eventQueryKeys.patientStatus(locationFilter, timezone),
    queryFn: () => getPatientStatus(locationFilter, timezone),
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    refetchOnMount: "always" as const,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  }),
  // Renamed: event-level checklist (deduped across services)
  appointmentChecklist: (eventId: string) => ({
    queryKey: eventQueryKeys.appointmentChecklist(eventId),
    queryFn: () => getAppointmentChecklist(eventId),
  }),
  eventMessages: (eventId: string) => ({
    queryKey: eventQueryKeys.eventMessages(eventId),
    queryFn: () => getEventMessages(eventId),
  }),
  messageTypes: (eventId: string) => ({
    queryKey: eventQueryKeys.messageTypes(eventId),
    queryFn: () => getEventMessageTypes(eventId),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: false,
  }),
  messageTypesBatch: (eventIds: string[]) => ({
    queryKey: eventQueryKeys.messageTypesBatch(eventIds),
    queryFn: () => getEventMessageTypesBatch(eventIds),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }),
  detail: (eventId: string) => ({
    queryKey: eventQueryKeys.detail(eventId),
    queryFn: () => getEventDetail(eventId),
  }),
  updateStatus: (eventId: string, status: EventStatus) => ({
    queryKey: eventQueryKeys.updateStatus(eventId, status),
    queryFn: () => updateEventStatus(eventId, status as EventStatus),
  }),
  authorizeGoogleCalendar: () => ({
    queryKey: eventQueryKeys.authorizeGoogleCalendar(),
    queryFn: authorizeGoogleCalendar,
  }),
  authorizeTenantGoogleCalendar: () => ({
    queryKey: eventQueryKeys.authorizeTenantGoogleCalendar(),
    queryFn: authorizeTenantGoogleCalendar,
  }),
  conflictedShiftIds: (start: Date, end: Date) => ({
    queryKey: eventQueryKeys.conflictedShiftIds(start, end),
    queryFn: () => getConflictedShiftIds(start, end),
  }),
  shiftBreakPlan: (locationId: string | null, start: Date, end: Date) => ({
    queryKey: eventQueryKeys.shiftBreakPlan(locationId, start, end),
    queryFn: () =>
      locationId
        ? getShiftBreakPlan(locationId, start, end)
        : Promise.resolve(null),
  }),
  shiftBreaks: (
    providerId: string | null,
    locationId: string | null,
    start: Date | null,
    end: Date | null,
  ) => ({
    queryKey: eventQueryKeys.shiftBreaks(providerId, locationId, start, end),
    queryFn: () =>
      providerId && locationId && start && end
        ? listShiftBreaks(providerId, locationId, start, end)
        : Promise.resolve([]),
  }),
  /** `timeZone` is the clock `date` is a day on; the tenant's when omitted. */
  shiftsByDate: (date: Date | null, timeZone?: string) => ({
    queryKey: eventQueryKeys.shiftsByDate(date, timeZone),
    queryFn: () =>
      date ? getShiftsByDate(date, timeZone) : Promise.resolve(null),
  }),
  /**
   * Query options for fetching one calendar window of events for the selected
   * locations and providers. Event type filtering is done client-side.
   *
   * IDs are sorted and includeUnassigned is part of the key so the same
   * semantic location/provider filter always maps to exactly one cache entry.
   * The "locs:provs" key string is parsed by eventMatchesByLocationQuery,
   * which is order-independent but relies on the "__empty__" sentinel —
   * preserve it when changing the key format.
   */
  byLocationAndProvider: (
    locationIds: string[],
    providerIds: string[],
    start?: Date,
    end?: Date,
    includeUnassigned: boolean = true,
  ) => {
    const locationFilter = canonicalCalendarLocationFilter({
      locationIds,
      includeUnassigned,
    });
    const sortedLocationIds = locationFilter.locationIds ?? [];
    const sortedProviderIds = [...providerIds].sort();
    return {
      queryKey: eventQueryKeys.byLocation(
        `${sortedLocationIds.join(",") || "__empty__"}:${sortedProviderIds.join(",") || "__empty__"}`,
        start,
        end,
        includeUnassigned,
      ),
      // Failures must reach React Query as errors. requestData maps AbortError
      // to null even with catchError=false, and a null committed as fresh data
      // would replace the kept-previous window with an empty calendar and
      // suppress recovery until staleTime expires — so map aborts back to a
      // throw here.
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const result = await listEventsByLocationAndProvider(
          sortedLocationIds,
          sortedProviderIds,
          locationFilter,
          start,
          end,
          signal,
        );
        if (signal.aborted) {
          throw signal.reason ?? new DOMException("Aborted", "AbortError");
        }
        return result;
      },
      // Mount refetch is a backstop, not the freshness path. The WebSocket
      // sync runs console-wide and keeps patching these caches while the
      // calendar is off screen, and WS/mutation invalidations use refetchType
      // "none". With a single active query this costs at most one request per
      // mount, and only once data is older than staleTime.
      refetchOnMount: true,
      // Window focus must NOT refetch (matches the app-wide default). The
      // WebSocket patches keep the active window fresh, and focus-refetch is
      // what used to herd every cached range into simultaneous requests.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // staleTime: how long data is "fresh" (no auto-refetch on mount or
      // reconnect). Calendar data doesn't change frequently and WebSocket
      // handles real-time updates for most changes.
      staleTime: 1000 * 60 * 2,
      // gcTime: how long inactive windows (previously viewed or prefetched
      // neighbors) stay cached for instant back-navigation. Kept short to
      // bound memory on long-lived tabs, and consistent with `list` above.
      gcTime: 1000 * 60 * 5,
    };
  },
  /**
   * Query options for fetching bookable usage within a date range.
   * Used by calendar preview to show conflicts.
   */
  bookableUsage: (
    bookableIds: string[],
    start?: Date,
    end?: Date,
    locationId?: string,
    enabled: boolean = true,
  ) => ({
    queryKey: eventQueryKeys.bookableUsage(bookableIds, start, end, locationId),
    queryFn: () => listBookableUsage(bookableIds, start, end, locationId),
    enabled: enabled && bookableIds.length > 0,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  }),
};

/** Optimistic mirror of EVENT_STATUS_MAP in decoda/services/care_plan.py */
const EVENT_TO_STEP_STATUS: Partial<Record<EventStatus, CarePlanStepStatus>> = {
  [EventStatus.COMPLETED]: CarePlanStepStatus.COMPLETED,
  [EventStatus.NO_SHOW]: CarePlanStepStatus.MISSED,
  [EventStatus.CANCELLED]: CarePlanStepStatus.SKIPPED,
};

type SiblingCacheSnapshot = { key: readonly unknown[]; previous: unknown };

/**
 * Whether `event` belongs to the given recurring series and falls within the
 * scope implied by `mode` (FUTURE = at or after `primaryStart`; ALL = any).
 *
 * Shared between optimistic flows that fan a series-level mutation across
 * cached siblings — e.g. cancellation status mirroring (this file) and the
 * recurring update flow that swaps old siblings out for new ones.
 */
export function isInRecurringSeriesScope(
  event: {
    recurringEventId?: string | null;
    recurringEvent?: { id?: string } | null;
    start?: string | null;
  },
  seriesId: string,
  mode: RecurrenceModificationMode,
  primaryStart: string | Date,
): boolean {
  const eRecurringId =
    event.recurringEventId ?? event.recurringEvent?.id ?? null;
  if (eRecurringId !== seriesId) return false;
  if (mode === RecurrenceModificationMode.FUTURE) {
    if (!event.start || toMs(event.start) < toMs(primaryStart)) return false;
  }
  return true;
}

/**
 * Optimistically retire recurring siblings in every cache that holds them
 * (range/calendar queries, list queries, patient event lists, per-event
 * tiny/detail), mirroring what the server does to them. FUTURE archives the
 * siblings, so they leave the caches entirely; ALL cancels each in place, so
 * they stay put with a CANCELLED status. We deliberately avoid a broad
 * `invalidateQueries` here because that forces every calendar range query in
 * memory to refetch — expensive on large schedules. Optimistic mirroring keeps
 * the UI consistent without a refetch storm; if the request fails we restore the
 * snapshots in onError.
 */
function optimisticCancelRecurringSiblings(
  primary: {
    id: string;
    recurringEventId?: string | null;
    start?: string | null;
  },
  mode: RecurrenceModificationMode,
  patientId?: string,
): SiblingCacheSnapshot[] {
  if (
    mode !== RecurrenceModificationMode.FUTURE &&
    mode !== RecurrenceModificationMode.ALL
  ) {
    return [];
  }
  const seriesId = primary.recurringEventId;
  if (!seriesId) return [];

  const primaryStart = primary.start || new Date(0);
  const archivesSiblings = mode === RecurrenceModificationMode.FUTURE;
  // Mirror `list_sibling_events(only_active=True)`: the server leaves an
  // already-cancelled occurrence alone, so removing it here would make a row
  // vanish from the calendar that is still very much in the database.
  const isSibling = (e: {
    id: string;
    status?: EventStatus;
    recurringEventId?: string | null;
    recurringEvent?: { id?: string } | null;
    start?: string | null;
  }): boolean =>
    e.id !== primary.id &&
    !(archivesSiblings && e.status === EventStatus.CANCELLED) &&
    isInRecurringSeriesScope(e, seriesId, mode, primaryStart);

  const snapshots: SiblingCacheSnapshot[] = [];

  // Walk every cache that stores arrays of EventTiny / EventDetail by key prefix
  // and retire matching siblings. byLocations covers the calendar grid; lists
  // covers shift management / list pages.
  const walkArrayCaches = <T extends EventTiny | EventDetail>(
    queryKey: readonly unknown[],
  ) => {
    const queries = queryClient.getQueriesData<T[]>({ queryKey, exact: false });
    for (const [key, data] of queries) {
      if (!data) continue;
      let modified = false;
      const next: T[] = [];
      for (const e of data) {
        if (!isSibling(e)) {
          next.push(e);
          continue;
        }
        modified = true;
        if (!archivesSiblings) {
          next.push({ ...e, status: EventStatus.CANCELLED });
        }
      }
      if (modified) {
        snapshots.push({ key, previous: data });
        queryClient.setQueryData(key, next);
      }
    }
  };

  walkArrayCaches<EventTiny>(eventQueryKeys.byLocations());
  walkArrayCaches<EventTiny>(eventQueryKeys.lists());
  if (patientId) {
    walkArrayCaches<EventDetail>(
      eventQueryKeys.searchPatientEvents(patientId, ""),
    );
  }

  // Per-sibling tiny + detail caches. Walk those caches directly rather than
  // reusing ids harvested above: a sibling the user opened once but that sits in
  // no currently-cached range has an entry here and nowhere else. Cancelled
  // siblings still exist, so they take the new status; archived ones are gone,
  // so the entry is removed outright — `setQueryData(key, undefined)` returns
  // early without touching the cache, which would strand the stale record.
  const walkSingleCaches = <T extends EventTiny | EventDetail>(
    queryKey: readonly unknown[],
  ) => {
    const queries = queryClient.getQueriesData<T>({ queryKey, exact: false });
    for (const [key, data] of queries) {
      if (!data || !isSibling(data)) continue;
      snapshots.push({ key, previous: data });
      if (archivesSiblings) {
        queryClient.removeQueries({ queryKey: key, exact: true });
      } else {
        queryClient.setQueryData<T>(key, {
          ...data,
          status: EventStatus.CANCELLED,
        });
      }
    }
  };

  walkSingleCaches<EventTiny>(eventQueryKeys.tinies());
  walkSingleCaches<EventDetail>(eventQueryKeys.details());

  return snapshots;
}

/**
 * Optimistically update any cached care plan steps linked to the given event.
 * Returns a snapshot so the caller can revert on error.
 */
function optimisticUpdateCarePlanSteps(
  eventId: string,
  stepStatus: CarePlanStepStatus,
): Map<readonly unknown[], CarePlanDetail> {
  const snapshots = new Map<readonly unknown[], CarePlanDetail>();
  const allCarePlanQueries = queryClient.getQueriesData<CarePlanDetail>({
    queryKey: carePlanQueryKeys.all,
  });
  for (const [key, data] of allCarePlanQueries) {
    if (!data?.steps?.some((s) => s.eventId === eventId)) continue;
    snapshots.set(key, data);
    queryClient.setQueryData<CarePlanDetail>(key, {
      ...data,
      steps: data.steps.map((s) =>
        s.eventId === eventId ? { ...s, status: stepStatus } : s,
      ),
    });
  }
  return snapshots;
}

export const useUpdateEventStatusMutation = () => {
  const showGoogleCalendarWarning = useGoogleCalendarPartialSuccessToast();

  return useMutation<
    EventStatusUpdateResponse | null,
    Error,
    {
      eventId: string;
      patientId?: string;
      newStatus: EventStatus;
      cancellationReason?: string;
      sendSms?: boolean;
      cancellationMessage?: string;
      recurrenceModificationMode?: RecurrenceModificationMode;
      chargeCancellationFee?: boolean;
      chargeNoShowFee?: boolean;
    },
    {
      listKey?: readonly unknown[];
      listPrevious?: EventDetail[] | undefined;
      detailKey?: readonly unknown[];
      detailPrevious?: EventDetail | undefined;
      tinyKey?: readonly unknown[];
      tinyPrevious?: EventTiny | undefined;
      previousSidebarCounts?: PatientSidebarCounts;
      carePlanSnapshots?: Map<readonly unknown[], CarePlanDetail>;
      siblingSnapshots?: SiblingCacheSnapshot[];
    }
  >({
    mutationFn: async ({
      eventId,
      newStatus,
      cancellationReason,
      sendSms,
      cancellationMessage,
      recurrenceModificationMode,
      chargeCancellationFee,
      chargeNoShowFee,
    }) =>
      await updateEventStatus(
        eventId,
        newStatus,
        cancellationReason,
        sendSms,
        cancellationMessage,
        recurrenceModificationMode,
        chargeCancellationFee,
        chargeNoShowFee,
      ),
    onMutate: async ({
      eventId,
      patientId,
      newStatus,
      recurrenceModificationMode,
    }) => {
      let listKey: readonly unknown[] | undefined;
      let listPrevious: EventDetail[] | undefined;
      let previousSidebarCounts: PatientSidebarCounts | undefined;
      // Optimistically update tiny cache for this event
      const tinyKey = eventQueryKeys.tiny(eventId);
      const tinyRes = await startOptimisticChange<EventTiny | undefined>(
        tinyKey,
        () => {
          const tiny = queryClient.getQueryData<EventTiny | undefined>(tinyKey);
          if (tiny) {
            queryClient.setQueryData<EventTiny>(tinyKey, {
              ...tiny,
              status: newStatus,
            });
          }
        },
      );
      if (patientId) {
        listKey = eventQueryKeys.searchPatientEvents(patientId, "");
        const res = await startOptimisticChange<EventDetail[] | undefined>(
          listKey,
          () => {
            const curr = queryClient.getQueryData<EventDetail[] | undefined>(
              listKey as readonly unknown[],
            );
            const list = curr ?? [];
            queryClient.setQueryData<EventDetail[] | undefined>(
              listKey as readonly unknown[],
              list.map((e) =>
                e.id === eventId ? { ...e, status: newStatus } : e,
              ),
            );
          },
        );
        listPrevious = res.previous;
      }

      const detailKey = eventQueryKeys.detail(eventId);
      const detailRes = await startOptimisticChange<EventDetail | undefined>(
        detailKey,
        () => {
          const prev = queryClient.getQueryData<EventDetail | undefined>(
            detailKey,
          );
          if (prev) {
            queryClient.setQueryData<EventDetail>(detailKey, {
              ...prev,
              status: newStatus,
            });
          }
        },
      );
      const detailPrevious = detailRes.previous;

      // Adjust sidebar appointments count if we can determine upcoming change
      if (patientId) {
        const before = detailPrevious;
        const wasUpcoming = before?.start
          ? toMs(before.start) > Date.now()
          : undefined;
        if (wasUpcoming) {
          const wasActive =
            before?.status !== EventStatus.CANCELLED &&
            before?.status !== EventStatus.COMPLETED;
          const becomesInactive =
            newStatus === EventStatus.CANCELLED ||
            newStatus === EventStatus.COMPLETED;
          const becomesActive =
            !wasActive &&
            newStatus !== EventStatus.CANCELLED &&
            newStatus !== EventStatus.COMPLETED;
          if (wasActive && becomesInactive) {
            previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
              appointmentsCount: -1,
            });
          } else if (!wasActive && becomesActive) {
            previousSidebarCounts = optimisticAdjustSidebarCounts(patientId, {
              appointmentsCount: 1,
            });
          }
        }
      }

      // Optimistically update care plan steps linked to this event
      const stepStatus = EVENT_TO_STEP_STATUS[newStatus];
      const carePlanSnapshots = stepStatus
        ? optimisticUpdateCarePlanSteps(eventId, stepStatus)
        : undefined;

      // Recurring FUTURE/ALL cancellations also flip sibling occurrences server-side.
      // Mirror that change in cache so the UI reflects it without a refetch storm.
      const siblingSnapshots =
        newStatus === EventStatus.CANCELLED &&
        recurrenceModificationMode &&
        detailPrevious
          ? optimisticCancelRecurringSiblings(
              {
                id: eventId,
                recurringEventId: detailPrevious.recurringEventId,
                start: detailPrevious.start,
              },
              recurrenceModificationMode,
              patientId,
            )
          : undefined;

      return {
        listKey,
        listPrevious,
        detailKey,
        detailPrevious,
        tinyKey,
        tinyPrevious: tinyRes.previous,
        previousSidebarCounts,
        carePlanSnapshots,
        siblingSnapshots,
      } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.listKey) revertOnError(ctx.listKey, ctx.listPrevious);
      if (ctx?.detailKey) revertOnError(ctx.detailKey, ctx.detailPrevious);
      if (ctx?.tinyKey) revertOnError(ctx.tinyKey, ctx.tinyPrevious);
      if (ctx?.carePlanSnapshots) {
        ctx.carePlanSnapshots.forEach((prev, key) => {
          queryClient.setQueryData(key, prev);
        });
      }
      if (ctx?.siblingSnapshots) {
        for (const { key, previous } of ctx.siblingSnapshots) {
          queryClient.setQueryData(key, previous);
        }
      }
      if (_vars.patientId)
        revertSidebarCounts(_vars.patientId, ctx?.previousSidebarCounts);
      toast({
        title: "Error",
        description: "Failed to update appointment status. Please try again.",
        variant: "error",
      });
    },
    onSuccess: (response, variables) => {
      const result = response?.event;
      const { eventId, patientId } = variables;
      const status = result?.status;
      if (status) {
        // Update all calendar caches (range queries, tiny)
        updateEventStatusInCalendarCaches(eventId, status);

        // Also update detail cache
        const detailKey = eventQueryKeys.detail(eventId);
        queryClient.setQueryData<EventDetail | undefined>(detailKey, (curr) =>
          curr ? { ...curr, status } : curr,
        );
      }
      if (patientId && status) {
        const listKey = eventQueryKeys.searchPatientEvents(patientId, "");
        queryClient.setQueryData<EventDetail[] | undefined>(
          listKey,
          (current) => {
            const list = current ?? [];
            return list.map((e) => (e.id === eventId ? { ...e, status } : e));
          },
        );
      }

      // Invalidate event log history to show new log entries
      queryClient.invalidateQueries(eventLogQueryOptions.list(eventId));

      // Sync care plan caches with server state
      if (EVENT_TO_STEP_STATUS[status ?? variables.newStatus]) {
        queryClient.invalidateQueries({ queryKey: carePlanQueryKeys.all });
      }

      if (response?.googleCalendarFailures.length) {
        showGoogleCalendarWarning(
          response.googleCalendarFailures,
          variables.newStatus === EventStatus.CANCELLED
            ? "cancelled"
            : "updated",
        );
      }

      // FUTURE/ALL sibling cancellations were already mirrored optimistically
      // in onMutate, so no broad invalidation is needed here.
    },
  });
};

/**
 * Determines whether an appointment should be auto-completed based on
 * checklist item state and per-service settings.
 *
 * Returns "complete" if the appointment should auto-complete, "no-op" otherwise.
 */
export function shouldAutoComplete(
  items: AppointmentChecklistItem[],
  servicesById: Map<string, { allowLastItemCompletion?: boolean }> | undefined,
  currentTenant: string,
): "complete" | "no-op" {
  if (!items || items.length === 0) return "no-op";

  const mandatoryItems = items.filter((i) => !i.optional);
  const completedAllMandatoryItems =
    mandatoryItems.length > 0 &&
    mandatoryItems.every((i) => Boolean(i.completedDate));

  // The step exists to hold the visit open, so the last-item shortcut never skips it.
  if (
    mandatoryItems.some(
      (i) =>
        i.type === AppointmentChecklistType.NOTE_SIGNED_OFF && !i.completedDate,
    )
  ) {
    return "no-op";
  }

  const lastItemCompleted = (() => {
    if (currentTenant === "rewind") return false;
    // Profile Photo is often ticked before the visit starts, so it never counts as the last step.
    const sorted = items
      .filter((i) => i.type !== AppointmentChecklistType.PROFILE_PHOTO)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const last = sorted[sorted.length - 1];
    return Boolean(last?.completedDate);
  })();

  const anyServiceDisallowsLastItem = items.some((i) => {
    const svcId = i.serviceId as string | undefined;
    return svcId && servicesById?.get(svcId)?.allowLastItemCompletion === false;
  });

  if (anyServiceDisallowsLastItem) {
    return completedAllMandatoryItems ? "complete" : "no-op";
  }
  return completedAllMandatoryItems || lastItemCompleted ? "complete" : "no-op";
}

export const useUpdateAppointmentChecklistMutation = (
  servicesById?: Map<string, { allowLastItemCompletion?: boolean }>,
) => {
  const { getCurrentTenant } = useTenantStore();
  const updateEventStatusMutation = useUpdateEventStatusMutation();
  return useMutation<
    boolean | null,
    Error,
    {
      appointmentChecklistId: string;
      eventId: string;
      serviceId?: string;
      completed: boolean;
      patientId?: string;
      optimisticCompletedByUser?: Pick<
        UserTiny,
        "id" | "firstName" | "lastName" | "type"
      >;
      source?:
        | "patient_status_panel"
        | "appointment_checklist"
        | "checklist_item_action"
        | "auto_form_completion"
        | "auto_care_plan_completion";
    },
    {
      key: readonly unknown[];
      previous?: AppointmentChecklistItem[];
      tinyKey?: readonly unknown[];
      tinyPrevious?: EventTiny | undefined;
      patientStatusPrevious?: [
        readonly unknown[],
        PatientStatusEventResponse[] | undefined,
      ][];
    }
  >({
    mutationFn: async ({ appointmentChecklistId, eventId, completed }) => {
      const completedDate = completed ? new Date() : undefined;
      return await completeAppointmentChecklist(
        appointmentChecklistId,
        eventId,
        completedDate,
      );
    },
    onMutate: async ({
      eventId,
      appointmentChecklistId,
      completed,
      optimisticCompletedByUser,
    }) => {
      const eventKey = eventQueryKeys.appointmentChecklist(eventId);

      const applyUpdate = (items: AppointmentChecklistItem[] | undefined) => {
        if (!items) return items;
        return items.map((item) => {
          if (item.id !== appointmentChecklistId) return item;
          const completedByUser: UserTiny | undefined = completed
            ? (item.completedByUser ??
              (optimisticCompletedByUser
                ? {
                    id: optimisticCompletedByUser.id,
                    firstName: optimisticCompletedByUser.firstName || "",
                    lastName: optimisticCompletedByUser.lastName,
                    type: optimisticCompletedByUser.type,
                  }
                : undefined))
            : undefined;
          return {
            ...item,
            completedDate: completed ? new Date().toISOString() : undefined,
            completedByUser,
          };
        });
      };

      const { previous } = await startOptimisticChange<
        AppointmentChecklistItem[] | undefined
      >(eventKey, () => {
        const prev = queryClient.getQueryData<
          AppointmentChecklistItem[] | undefined
        >(eventKey);
        if (prev) {
          queryClient.setQueryData<AppointmentChecklistItem[]>(
            eventKey,
            applyUpdate(prev),
          );
        }
      });

      // Optimistically update patient status cache
      const { previous: patientStatusPrevious } =
        updatePatientStatusCacheEntries(
          eventId,
          appointmentChecklistId,
          (item) => ({
            ...item,
            completedDate: completed ? new Date().toISOString() : undefined,
            completedByUser: completed
              ? (item.completedByUser ??
                (optimisticCompletedByUser
                  ? {
                      id: optimisticCompletedByUser.id,
                      firstName: optimisticCompletedByUser.firstName || "",
                      lastName: optimisticCompletedByUser.lastName,
                      type: optimisticCompletedByUser.type,
                    }
                  : undefined))
              : undefined,
          }),
          completed,
        );

      // Optimistically update icons for CHECKED_IN in all calendar caches
      let tinyKey: readonly unknown[] | undefined;
      let tinyPrevious: EventTiny | undefined;
      const prevItems = queryClient.getQueryData<
        AppointmentChecklistItem[] | undefined
      >(eventKey);
      const thisItem = prevItems?.find((i) => i.id === appointmentChecklistId);
      if (thisItem && thisItem.type === AppointmentChecklistType.CHECKED_IN) {
        tinyKey = eventQueryKeys.tiny(eventId);
        // Capture previous tiny state for rollback
        tinyPrevious = queryClient.getQueryData<EventTiny | undefined>(
          tinyKey as readonly unknown[],
        );

        // Update all calendar caches (range queries, tiny)
        updateEventIconsInCalendarCaches(eventId, (prevIcons) =>
          completed
            ? prevIcons.includes(EventIcon.CHECKED_IN)
              ? prevIcons
              : [...prevIcons, EventIcon.CHECKED_IN]
            : prevIcons.filter((i) => i !== EventIcon.CHECKED_IN),
        );
      }

      return {
        key: eventKey,
        previous,
        tinyKey,
        tinyPrevious,
        patientStatusPrevious,
      } as const;
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.key) revertOnError(ctx.key, ctx.previous);
      if (ctx?.tinyKey) revertOnError(ctx.tinyKey, ctx.tinyPrevious);
      if (ctx?.patientStatusPrevious) {
        for (const [queryKey, data] of ctx.patientStatusPrevious) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      posthog.capture("patient_status_step_failed", {
        event_id: _vars.eventId,
        checklist_item_id: _vars.appointmentChecklistId,
        completed: _vars.completed,
        source: _vars.source,
        patient_id: _vars.patientId,
        error_message: _err.message,
      });

      toast({
        title: "Error",
        description: "Failed to update checklist item. Please try again.",
        variant: "error",
      });
    },
    onSettled: (_res, _err, variables) => {
      invalidateByKey(eventQueryKeys.appointmentChecklist(variables.eventId));
      // Also refresh patient events if patientId provided
      if (variables.patientId) {
        invalidateByKey(
          eventQueryKeys.searchPatientEvents(variables.patientId, ""),
        );
      }
    },
    onSuccess: async (_res, variables) => {
      const {
        eventId,
        patientId,
        appointmentChecklistId,
        completed,
        source,
        optimisticCompletedByUser,
      } = variables;
      const key = eventQueryKeys.appointmentChecklist(eventId);
      const items = queryClient.getQueryData<
        AppointmentChecklistItem[] | undefined
      >(key);

      // Track checklist step completion/unchecking in PostHog
      const completedItem = items?.find((i) => i.id === appointmentChecklistId);
      if (completedItem) {
        if (completed) {
          const totalSteps = items?.length ?? 0;
          const completedSteps =
            items?.filter((i) => Boolean(i.completedDate)).length ?? 0;
          const mandatoryStepsRemaining =
            items?.filter((i) => !i.optional && !i.completedDate).length ?? 0;

          posthog.capture("patient_status_step_completed", {
            event_id: eventId,
            checklist_item_id: appointmentChecklistId,
            step_type: completedItem.type,
            step_name: completedItem.name,
            step_position: completedItem.position,
            is_optional: completedItem.optional,
            source,
            patient_id: patientId,
            completed_by_user_id: optimisticCompletedByUser?.id,
            total_steps: totalSteps,
            completed_steps: completedSteps,
            mandatory_steps_remaining: mandatoryStepsRemaining,
          });
        } else {
          posthog.capture("patient_status_step_unchecked", {
            event_id: eventId,
            checklist_item_id: appointmentChecklistId,
            step_type: completedItem.type,
            step_name: completedItem.name,
            step_position: completedItem.position,
            source,
            patient_id: patientId,
            unchecked_by_user_id: optimisticCompletedByUser?.id,
          });
        }
      }

      // Auto-confirm appointment when CHECKED_IN item is completed
      if (completed) {
        const checkedInItem = items?.find(
          (i) => i.id === appointmentChecklistId,
        );
        if (checkedInItem?.type === AppointmentChecklistType.CHECKED_IN) {
          const eventStatus =
            queryClient.getQueryData<EventTiny>(
              eventQueryKeys.tiny(eventId) as readonly unknown[],
            )?.status ??
            queryClient.getQueryData<EventDetail>(
              eventQueryKeys.detail(eventId),
            )?.status;
          if (
            eventStatus === EventStatus.PENDING ||
            eventStatus === EventStatus.RESERVED
          ) {
            await updateEventStatusMutation.mutateAsync({
              eventId,
              patientId,
              newStatus: EventStatus.CONFIRMED,
            });
          }
        }
      }

      // Auto-complete appointment status when all mandatory items are done
      // or the last item is completed.
      if (
        shouldAutoComplete(
          items ?? [],
          servicesById,
          getCurrentTenant() ?? "",
        ) === "complete"
      ) {
        await updateEventStatusMutation.mutateAsync({
          eventId,
          patientId,
          newStatus: EventStatus.COMPLETED,
        });
      }
    },
  });
};

interface ShiftDeletionListQueryParams {
  userIds?: string[];
  eventTypes?: EventType[];
}

function rangeQueryIncludesDeletedShiftProviders(
  queryKey: readonly unknown[],
  providerIds?: string[],
): boolean {
  const locationProviderSelection = queryKey[3];
  if (typeof locationProviderSelection !== "string") return true;

  const providerSelection = locationProviderSelection.split(":")[1];
  if (!providerSelection || providerSelection === "__empty__") return false;

  const queryProviderIds = providerSelection.split(",").filter(Boolean);
  if (queryProviderIds.length === 0) return false;
  return (
    !providerIds ||
    providerIds.some((providerId) => queryProviderIds.includes(providerId))
  );
}

function listQueryCanContainDeletedShifts(
  queryKey: readonly unknown[],
  providerIds?: string[],
): boolean {
  const params = queryKey[3] as ShiftDeletionListQueryParams | undefined;
  const canContainShifts =
    !params?.eventTypes?.length || params.eventTypes.includes(EventType.SHIFT);
  const includesDeletedProviders =
    !providerIds ||
    !params?.userIds?.length ||
    providerIds.some((providerId) => params.userIds?.includes(providerId));
  return canContainShifts && includesDeletedProviders;
}

type CancelQueryFilters = NonNullable<
  Parameters<typeof queryClient.cancelQueries>[0]
>;

async function cancelMatchingQueries(
  filters: CancelQueryFilters[],
): Promise<void> {
  await Promise.all(
    filters.map(({ predicate, ...filter }) =>
      queryClient.cancelQueries({
        ...filter,
        predicate: (query) => !predicate || predicate(query),
      }),
    ),
  );
}

async function cancelShiftQueries(providerIds?: string[]): Promise<void> {
  await cancelMatchingQueries([
    { queryKey: eventQueryKeys.shifts() },
    {
      queryKey: eventQueryKeys.lists(),
      predicate: ({ queryKey }) =>
        listQueryCanContainDeletedShifts(queryKey, providerIds),
    },
    {
      queryKey: eventQueryKeys.byLocations(),
      predicate: ({ queryKey }) =>
        rangeQueryIncludesDeletedShiftProviders(queryKey, providerIds),
    },
  ]);
}

async function refetchShiftQueriesAfterDeleteError(
  providerIds?: string[],
): Promise<void> {
  await Promise.all([
    queryClient.refetchQueries({
      queryKey: eventQueryKeys.shifts(),
      type: "active",
    }),
    queryClient.refetchQueries({
      queryKey: eventQueryKeys.lists(),
      type: "active",
      predicate: ({ queryKey }) =>
        listQueryCanContainDeletedShifts(queryKey, providerIds),
    }),
    queryClient.refetchQueries({
      queryKey: eventQueryKeys.byLocations(),
      type: "active",
      predicate: ({ queryKey }) =>
        rangeQueryIncludesDeletedShiftProviders(queryKey, providerIds),
    }),
  ]);
}

export function removeDeletedShiftIdsFromCaches(eventIds: Set<string>): void {
  if (eventIds.size === 0) return;

  for (const eventId of Array.from(eventIds)) {
    removeEventFromCalendarCaches(eventId);
  }
  removeEventsFromAllListCaches(eventIds);
  queryClient.setQueriesData<ShiftsByDateResponse>(
    { queryKey: eventQueryKeys.shifts(), exact: false },
    (response) => {
      if (!response) return response;
      const providers = response.providers
        .map((provider) => ({
          ...provider,
          shifts: provider.shifts.filter((shift) => !eventIds.has(shift.id)),
        }))
        .filter(
          (provider) =>
            provider.shifts.length > 0 || provider.appointments.length > 0,
        );
      return { ...response, providers };
    },
  );
}

export async function invalidateShiftConflictsForEventType(
  eventType: EventType | undefined,
): Promise<void> {
  if (eventType !== EventType.SHIFT) return;
  const queryKey = eventQueryKeys.allConflictedShifts();
  await queryClient.cancelQueries({ queryKey });
  await queryClient.invalidateQueries({ queryKey });
}

export function eventUpdateAffectsShiftConflicts(
  updates: EventUpdate,
): boolean {
  return (
    updates.start !== undefined ||
    updates.end !== undefined ||
    updates.attendeeIds !== undefined ||
    updates.locationId !== undefined
  );
}

export const useDeleteShiftsByProviderMutation = () => {
  return useMutation<
    DeleteShiftsResponse | null,
    Error,
    { date: Date; providerIds: string[] }
  >({
    mutationFn: async ({ date, providerIds }) =>
      await deleteShiftsByProvider(date, providerIds),
    onMutate: async ({ providerIds }) => {
      await cancelShiftQueries(providerIds);
    },
    onSuccess: async (result) => {
      if (!result) return;
      // A deleted shift may also live in caches filtered to another attendee.
      // Cancel every shift-capable cache before removing the authoritative IDs.
      await cancelShiftQueries();
      removeDeletedShiftIdsFromCaches(new Set(result.eventIds));
      applyShiftBreakChanges([], result.removedBreakEventIds);
      if (result.count > 0) {
        await invalidateShiftConflictsForEventType(EventType.SHIFT);
      }
    },
    onError: (_, { providerIds }) => {
      void refetchShiftQueriesAfterDeleteError(providerIds);
      toast({
        title: "Failed to Delete Shifts",
        description:
          "An error occurred while deleting shifts. Please try again.",
        variant: "error",
      });
    },
  });
};

export const useDeleteShiftsByDatesMutation = () => {
  return useMutation<DeleteShiftsResponse | null, Error, { dates: Date[] }>({
    mutationFn: async ({ dates }) => await deleteShiftsByDates(dates),
    onMutate: async () => {
      await cancelShiftQueries();
    },
    onSuccess: async (result) => {
      if (!result) return;
      await cancelShiftQueries();
      removeDeletedShiftIdsFromCaches(new Set(result.eventIds));
      applyShiftBreakChanges([], result.removedBreakEventIds);
      if (result.count > 0) {
        await invalidateShiftConflictsForEventType(EventType.SHIFT);
      }
    },
    onError: () => {
      void refetchShiftQueriesAfterDeleteError();
      toast({
        title: "Failed to Delete Shifts",
        description:
          "An error occurred while deleting shifts. Please try again.",
        variant: "error",
      });
    },
  });
};

// ============================================================================
// Shared Calendar Cache Helpers
// These helpers update all relevant caches: range queries and tiny cache
// Used by both WebSocket handlers and mutations for consistency
// ============================================================================

function eventMatchesByLocationQuery(
  event: EventTiny,
  queryKey: readonly unknown[],
): boolean {
  // Key format: ["calendar", "event", "by-location", "locId1,locId2:provId1,provId2", { start, end, includeUnassigned }]
  const locationProviderStr = queryKey?.[3] as string | undefined;
  const params = queryKey?.[4] as
    | { start?: string; end?: string; includeUnassigned?: boolean }
    | undefined;

  if (!locationProviderStr) return false;

  const [locationIdsStr = "__empty__", providerIdsStr = "__empty__"] =
    locationProviderStr.split(":");
  const locationIds =
    locationIdsStr === "__empty__"
      ? []
      : locationIdsStr.split(",").filter(Boolean);
  // A mixed window carries both real providers and the provider-free bookable
  // lane, encoded as an empty token sorted ahead of the ids (segment ",prov_1").
  // filter(Boolean) drops that empty token, so detect the bookable lane *before*
  // filtering — otherwise provider-free bookable blocks fall out of this query's
  // surgical cache updates. The all-empty "__empty__" segment is the pure
  // bookable lane (no real providers selected).
  const providerTokens =
    providerIdsStr === "__empty__" ? [] : providerIdsStr.split(",");
  const includesBookableLane =
    providerIdsStr === "__empty__" || providerTokens.includes("");
  const providerIds = providerTokens.filter(Boolean);

  const eventLocationId = event.location?.id;
  const eventProviderIds = event.attendees?.map((a) => a.user.id) ?? [];

  const isBookableBlock = isBookableBlockEvent(event);
  const matchesProvider =
    (providerIds.length > 0 &&
      eventProviderIds.some((pid) => providerIds.includes(pid))) ||
    (includesBookableLane && isBookableBlock);

  const includeUnassigned = params?.includeUnassigned ?? true;
  const matchesLocation = locationIds.length
    ? eventLocationId
      ? locationIds.includes(eventLocationId)
      : includeUnassigned
    : includeUnassigned
      ? !eventLocationId
      : true;

  const eventStart = toMs(event.start);
  const eventEnd = toMs(event.end);
  const rangeStart = params?.start ? toMs(params.start) : 0;
  const rangeEnd = params?.end ? toMs(params.end) : Infinity;
  // All-day GCAL events are stored with start === end (zero duration), so the
  // half-open overlap below would drop one sitting exactly on rangeStart. Treat
  // a zero-duration event as a point contained in [rangeStart, rangeEnd).
  const matchesDateRange =
    eventStart === eventEnd
      ? eventStart >= rangeStart && eventStart < rangeEnd
      : eventStart < rangeEnd && eventEnd > rangeStart;

  return matchesLocation && matchesProvider && matchesDateRange;
}

/**
 * Update an event in all calendar caches (range queries, tiny).
 * Preserves icons from existing cached event.
 */
export function updateEventInCalendarCaches(updatedEvent: EventTiny): void {
  const tinyPrevious = queryClient.getQueryData<EventTiny>(
    eventQueryKeys.tiny(updatedEvent.id),
  );
  let preservedIcons = tinyPrevious?.icons ?? updatedEvent.icons;

  const allRangeQueries = queryClient.getQueriesData<EventTiny[]>({
    queryKey: eventQueryKeys.byLocations(),
  });

  for (const [, data] of allRangeQueries) {
    const existingEvent = data?.find((event) => event.id === updatedEvent.id);
    if (existingEvent) {
      preservedIcons = existingEvent.icons;
      break;
    }
  }

  for (const [queryKey, data] of allRangeQueries) {
    if (!data) continue;

    const existingEvent = data.find((event) => event.id === updatedEvent.id);
    const matchesQuery = eventMatchesByLocationQuery(updatedEvent, queryKey);

    if (existingEvent) {
      const eventWithPreservedIcons = {
        ...updatedEvent,
        icons: existingEvent.icons,
      };
      const nextData = matchesQuery
        ? data.map((event) =>
            event.id === updatedEvent.id ? eventWithPreservedIcons : event,
          )
        : data.filter((event) => event.id !== updatedEvent.id);

      queryClient.setQueryData<EventTiny[]>(queryKey, nextData);
    } else if (matchesQuery) {
      queryClient.setQueryData<EventTiny[]>(queryKey, [
        ...data,
        { ...updatedEvent, icons: preservedIcons },
      ]);
    }
  }

  // Update tiny cache
  queryClient.setQueryData<EventTiny>(eventQueryKeys.tiny(updatedEvent.id), {
    ...updatedEvent,
    icons: preservedIcons,
  });
}

/**
 * Remove an event from all calendar caches (range queries, tiny).
 */
export function removeEventFromCalendarCaches(eventId: string): void {
  // Remove from all range query caches
  queryClient.setQueriesData<EventTiny[]>(
    { queryKey: eventQueryKeys.byLocations(), exact: false },
    (oldData) => oldData?.filter((e) => e.id !== eventId),
  );

  // Remove tiny cache
  queryClient.removeQueries({
    queryKey: eventQueryKeys.tiny(eventId),
  });
}

/**
 * Update event status in all calendar caches (range queries, tiny).
 * Preserves all other event fields.
 */
export function updateEventStatusInCalendarCaches(
  eventId: string,
  status: EventStatus,
): void {
  const updateStatus = (event: EventTiny): EventTiny => ({
    ...event,
    status,
  });

  // Update all range query caches
  queryClient.setQueriesData<EventTiny[]>(
    { queryKey: eventQueryKeys.byLocations(), exact: false },
    (oldData) => oldData?.map((e) => (e.id === eventId ? updateStatus(e) : e)),
  );

  // Update tiny cache
  queryClient.setQueryData<EventTiny>(eventQueryKeys.tiny(eventId), (prev) =>
    prev ? updateStatus(prev) : prev,
  );
}

/**
 * Update event icons in all calendar caches (range queries, tiny).
 */
export function updateEventIconsInCalendarCaches(
  eventId: string,
  updateIcons: (prevIcons: EventIcon[]) => EventIcon[],
): void {
  const applyIconUpdate = (event: EventTiny): EventTiny => ({
    ...event,
    icons: updateIcons(event.icons || []),
  });

  // Update all range query caches
  queryClient.setQueriesData<EventTiny[]>(
    { queryKey: eventQueryKeys.byLocations(), exact: false },
    (oldData) =>
      oldData?.map((e) => (e.id === eventId ? applyIconUpdate(e) : e)),
  );

  // Update tiny cache
  queryClient.setQueryData<EventTiny>(eventQueryKeys.tiny(eventId), (prev) =>
    prev ? applyIconUpdate(prev) : prev,
  );
}

function invalidateTodaysItemsForEventPatients(
  events: readonly EventTiny[],
): void {
  const patientIds = new Set(
    events
      .flatMap((event) => event.attendees)
      .filter((attendee) => attendee.user.type === UserType.PATIENT)
      .map((attendee) => attendee.user.id),
  );

  for (const patientId of Array.from(patientIds)) {
    queryClient.invalidateQueries({
      queryKey: todaysItemsQueryKeys.patient(patientId),
    });
  }
}

/**
 * Add a new event to matching range query caches and tiny cache.
 * Checks each range query's parameters to see if the event should be included.
 */
export function addEventToCalendarCache(newEvent: EventTiny): void {
  // Add to all range query caches where the event might belong
  const allRangeQueries = queryClient.getQueriesData<EventTiny[]>({
    queryKey: eventQueryKeys.byLocations(),
  });

  for (const [queryKey, data] of allRangeQueries) {
    if (!data) continue;

    // Skip if event already exists in this cache
    if (data.some((e) => e.id === newEvent.id)) continue;

    if (eventMatchesByLocationQuery(newEvent, queryKey)) {
      queryClient.setQueryData<EventTiny[]>(queryKey, [...data, newEvent]);
    }
  }

  // Update tiny cache
  queryClient.setQueryData<EventTiny>(
    eventQueryKeys.tiny(newEvent.id),
    newEvent,
  );
}

/**
 * Helper to remove events from ALL list caches.
 * Since we don't know which list cache contains the event, we update all of them.
 */
function removeEventsFromAllListCaches(eventIds: Set<string>): void {
  const listsQueryKey = eventQueryKeys.lists();
  const allListQueries = queryClient.getQueriesData<EventTiny[]>({
    queryKey: listsQueryKey,
  });

  for (const [queryKey, data] of allListQueries) {
    if (!data) continue;
    const filteredData = data.filter((event) => !eventIds.has(event.id));
    // Only update if something was actually removed
    if (filteredData.length !== data.length) {
      queryClient.setQueryData<EventTiny[]>(queryKey, filteredData);
    }
  }
}

export function reconcileEventsInAllListCaches(
  updatedEvents: EventTiny[],
): void {
  const preservedIcons = new Map<string, EventIcon[]>();
  for (const [, events] of queryClient.getQueriesData<EventTiny[]>({
    queryKey: eventQueryKeys.lists(),
  })) {
    for (const event of events ?? []) {
      if (!preservedIcons.has(event.id)) {
        preservedIcons.set(event.id, event.icons);
      }
    }
  }

  removeEventsFromAllListCaches(
    new Set(updatedEvents.map((event) => event.id)),
  );
  addEventsToMatchingListCaches(
    updatedEvents.map((event) => ({
      ...event,
      icons: preservedIcons.get(event.id) ?? event.icons,
    })),
  );
}

interface EventListQueryParams {
  start?: string;
  end?: string;
  userIds?: string[];
  locationIds?: string[];
  includeUnassigned?: boolean;
  eventTypes?: EventType[];
}

function eventMatchesListQuery(
  event: EventTiny,
  params: EventListQueryParams,
): boolean {
  const matchesDateRange =
    (!params.start || toMs(event.end) > toMs(params.start)) &&
    (!params.end || toMs(event.start) < toMs(params.end));
  const matchesEventType =
    !params.eventTypes?.length || params.eventTypes.includes(event.type);
  const matchesUser =
    !params.userIds?.length ||
    event.attendees.some((attendee) =>
      params.userIds?.includes(attendee.user.id),
    ) ||
    isBookableBlockEvent(event);
  const matchesLocation = params.locationIds?.length
    ? event.location?.id
      ? params.locationIds.includes(event.location.id)
      : params.includeUnassigned === true
    : params.includeUnassigned === true
      ? !event.location?.id
      : true;

  return matchesDateRange && matchesEventType && matchesUser && matchesLocation;
}

/** Add events to list caches whose API filters include them. */
function addEventsToMatchingListCaches(newEvents: EventTiny[]): void {
  const allListQueries = queryClient.getQueriesData<EventTiny[]>({
    queryKey: eventQueryKeys.lists(),
  });

  for (const [queryKey, data] of allListQueries) {
    if (!data) continue;
    const params = queryKey[3] as EventListQueryParams | undefined;
    if (!params) continue;

    const existingIds = new Set(data.map((event) => event.id));
    const eventsToAdd = newEvents.filter(
      (event) =>
        !existingIds.has(event.id) && eventMatchesListQuery(event, params),
    );
    if (eventsToAdd.length > 0) {
      queryClient.setQueryData<EventTiny[]>(queryKey, [
        ...data,
        ...eventsToAdd,
      ]);
    }
  }
}

export function replaceRecurringEventsInCaches(
  newEvents: EventTiny[],
  originalEventId: string,
  oldRecurringEventId: string | undefined,
  recurrenceModificationMode: RecurrenceModificationMode,
  originalEventStart: string | Date,
): void {
  const shouldRemove = (event: EventTiny) => {
    if (event.id === originalEventId) return true;
    if (!oldRecurringEventId) return false;
    return isInRecurringSeriesScope(
      event,
      oldRecurringEventId,
      recurrenceModificationMode,
      originalEventStart,
    );
  };
  const removedEventIds = new Set([originalEventId]);

  for (const prefix of [eventQueryKeys.byLocations(), eventQueryKeys.lists()]) {
    for (const [, events] of queryClient.getQueriesData<EventTiny[]>({
      queryKey: prefix,
    })) {
      for (const event of events ?? []) {
        if (shouldRemove(event)) removedEventIds.add(event.id);
      }
    }
    queryClient.setQueriesData<EventTiny[]>(
      { queryKey: prefix, exact: false },
      (events) => events?.filter((event) => !shouldRemove(event)),
    );
  }

  for (const eventId of Array.from(removedEventIds)) {
    queryClient.removeQueries({
      queryKey: eventQueryKeys.tiny(eventId),
    });
  }
  for (const event of newEvents) {
    addEventToCalendarCache(event);
  }
  addEventsToMatchingListCaches(newEvents);
}

function showGoogleCalendarPartialSuccessToast(
  failures: GoogleCalendarSyncFailure[],
  canViewTenantSettings: boolean,
  navigate: (path: string) => void,
  actionPastTense: string,
) {
  const title = `Appointment ${actionPastTense}, but Google Calendar needs reconnecting`;
  const owners = new Set(failures.map((failure) => failure.owner));
  const calendarNotEnabled = (owner: GoogleCalendarSyncOwner) =>
    failures.some(
      (failure) =>
        failure.owner === owner && failure.reason === "calendar_not_enabled",
    );
  if (owners.has(GoogleCalendarSyncOwner.TENANT)) {
    const tenantDescription = calendarNotEnabled(GoogleCalendarSyncOwner.TENANT)
      ? "The clinic's connected Google account doesn't have Google Calendar turned on, so no calendar event or Google Meet link was created."
      : "The clinic-wide Google Calendar account could not be updated.";
    if (canViewTenantSettings) {
      toast({
        title,
        description: tenantDescription,
        variant: "warning",
        action: {
          label: "Reconnect",
          altText: "Reconnect clinic Google Calendar",
          onClick: () => navigate("/settings/clinic-google-calendar"),
        },
      });
    } else {
      toast({
        title,
        description: `${tenantDescription} Please contact your administrator.`,
        variant: "warning",
      });
    }
  }

  if (owners.has(GoogleCalendarSyncOwner.PROVIDER)) {
    toast({
      title,
      description: calendarNotEnabled(GoogleCalendarSyncOwner.PROVIDER)
        ? "Your connected Google account doesn't have Google Calendar turned on, so no calendar event or Google Meet link was created."
        : "Your Google Calendar account could not be updated.",
      variant: "warning",
      action: {
        label: "Reconnect",
        altText: "Reconnect your Google Calendar",
        onClick: () => navigate("/settings/google-calendar"),
      },
    });
  }
}

function useGoogleCalendarPartialSuccessToast() {
  const { navigateWithProgress } = useProgressNavigation();
  const { currentUser } = useUser();
  const canViewTenantSettings = hasPermission(
    currentUser,
    PERMISSIONS.SETTINGS_READ,
  );

  return (failures: GoogleCalendarSyncFailure[], actionPastTense: string) => {
    if (failures.length === 0) return;
    showGoogleCalendarPartialSuccessToast(
      failures,
      canViewTenantSettings,
      navigateWithProgress,
      actionPastTense,
    );
  };
}

/**
 * Mutation hook for creating calendar events (appointments, shifts, blocks).
 * Adds events to range query caches on success.
 */
async function cancelEventQueriesForEvents(events: EventTiny[]): Promise<void> {
  const eventIds = new Set(events.map((event) => event.id));
  const queryContainsEvent = (data: unknown) =>
    Array.isArray(data) &&
    data.some(
      (event) =>
        typeof event === "object" &&
        event !== null &&
        "id" in event &&
        eventIds.has(String(event.id)),
    );

  await cancelMatchingQueries([
    {
      queryKey: eventQueryKeys.byLocations(),
      predicate: ({ queryKey, state }) =>
        queryContainsEvent(state.data) ||
        events.some((event) => eventMatchesByLocationQuery(event, queryKey)),
    },
    {
      queryKey: eventQueryKeys.lists(),
      predicate: ({ queryKey, state }) => {
        const params = queryKey[3] as EventListQueryParams | undefined;
        return (
          queryContainsEvent(state.data) ||
          (!!params &&
            events.some((event) => eventMatchesListQuery(event, params)))
        );
      },
    },
  ]);
}

/** Show or drop the break blocks a shift save created or removed. */
function applyShiftBreakChanges(
  added: EventTiny[] = [],
  removedIds: string[] = [],
): void {
  for (const id of removedIds) removeEventFromCalendarCaches(id);
  if (removedIds.length > 0) removeEventsFromAllListCaches(new Set(removedIds));
  for (const event of added) addEventToCalendarCache(event);
  if (added.length > 0) addEventsToMatchingListCaches(added);
}

export const useCreateEventMutation = () => {
  const showGoogleCalendarWarning = useGoogleCalendarPartialSuccessToast();

  return useMutation<
    EventCreateResponse | null,
    Error,
    {
      event: EventCreate;
      timezone: string;
      sendNotifications?: boolean;
      sendSmsConfirmation?: boolean;
    }
  >({
    mutationFn: async ({
      event,
      timezone,
      sendNotifications = true,
      sendSmsConfirmation,
    }) => createEvent(event, timezone, sendNotifications, sendSmsConfirmation),

    onError: async (error, variables) => {
      const errorMessage = await parseError(error);
      toast({
        title: `Error creating ${variables.event.type.toLowerCase()}`,
        description:
          typeof errorMessage === "string"
            ? errorMessage
            : `Failed to create ${variables.event.type.toLowerCase()}`,
        variant: "error",
      });
    },

    onSuccess: async (result, variables) => {
      if (!result) return;
      const createdEvents = result.events;

      // Stop older loaded reads from overwriting the authoritative response.
      // First-load queries have no cache state to reconcile, so let them finish.
      await cancelEventQueriesForEvents(createdEvents);
      for (const createdEvent of createdEvents) {
        addEventToCalendarCache(createdEvent);
      }
      addEventsToMatchingListCaches(createdEvents);
      applyShiftBreakChanges(result.breakEvents);
      invalidateTodaysItemsForEventPatients(createdEvents);

      if (createdEvents.length > 0) {
        await invalidateShiftConflictsForEventType(variables.event.type);
      }

      if (
        result.status === EventCreateStatus.PARTIAL_SUCCESS &&
        result.googleCalendarFailures.length > 0
      ) {
        showGoogleCalendarWarning(result.googleCalendarFailures, "created");
      }
    },
  });
};

interface OptimisticEventSnapshot<T> {
  previous: T;
  optimistic: T;
  changedKeys: Array<keyof T>;
}

export function rollbackOptimisticFields<T extends object>(
  current: T,
  snapshot: OptimisticEventSnapshot<T>,
): T {
  let next = current;
  for (const key of snapshot.changedKeys) {
    if (current[key] !== snapshot.optimistic[key]) continue;
    if (next === current) next = { ...current };
    next[key] = snapshot.previous[key];
  }
  return next;
}

/**
 * Mutation hook for updating calendar events.
 * Handles both single events and recurring event series (FUTURE/ALL modes).
 * Updates range query caches and tiny cache optimistically for single events.
 * For recurring updates, old events are removed and new events are added on success.
 */
export const useUpdateEventMutation = () => {
  const showGoogleCalendarWarning = useGoogleCalendarPartialSuccessToast();

  return useMutation<
    EventUpdateResponse | null,
    Error,
    {
      updates: EventUpdate;
    },
    {
      rangeEventSnapshots?: Map<
        readonly unknown[],
        OptimisticEventSnapshot<EventTiny>
      >;
      tinySnapshot?: OptimisticEventSnapshot<EventTiny>;
      detailSnapshot?: OptimisticEventSnapshot<EventDetail>;
      originalEvent?: EventTiny;
    }
  >({
    mutationFn: async ({ updates }) => updateEvent(updates),

    onMutate: async ({ updates }) => {
      const { id: eventId, recurrenceModificationMode } = updates;
      const isRecurringUpdate =
        recurrenceModificationMode &&
        [
          RecurrenceModificationMode.ALL,
          RecurrenceModificationMode.FUTURE,
        ].includes(recurrenceModificationMode);

      // Recurring updates do not patch optimistically, so defer cancellation
      // until success rather than disrupting active reads when the request fails.
      if (!isRecurringUpdate) {
        await Promise.all([
          queryClient.cancelQueries({
            queryKey: eventQueryKeys.byLocations(),
          }),
          queryClient.cancelQueries({
            queryKey: eventQueryKeys.tiny(eventId),
          }),
          queryClient.cancelQueries({
            queryKey: eventQueryKeys.detail(eventId),
          }),
        ]);
      }

      const allRangeQueries = queryClient.getQueriesData<EventTiny[]>({
        queryKey: eventQueryKeys.byLocations(),
      });
      const tinyPrevious = queryClient.getQueryData<EventTiny>(
        eventQueryKeys.tiny(eventId),
      );
      const detailPrevious = queryClient.getQueryData<EventDetail>(
        eventQueryKeys.detail(eventId),
      );

      // Capture the original event even when the edit was opened from a detail
      // query rather than a mounted calendar range.
      let originalEvent: EventTiny | undefined;
      for (const [, data] of allRangeQueries) {
        const found = data?.find((event) => event.id === eventId);
        if (found) {
          originalEvent = found;
          break;
        }
      }
      originalEvent ??= tinyPrevious ?? detailPrevious;

      // For recurring updates, skip optimistic updates since we don't know
      // which events will be regenerated until the server responds.
      if (isRecurringUpdate) return { originalEvent };

      // Clear room when location actually changes without an explicit room.
      const shouldClearRoom =
        updates.locationId !== undefined &&
        updates.roomId === undefined &&
        updates.locationId !== originalEvent?.location?.id;
      const partialUpdate = {
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.start !== undefined && {
          start: updates.start.toISOString(),
        }),
        ...(updates.end !== undefined && { end: updates.end.toISOString() }),
        ...(updates.roomId !== undefined && { roomId: updates.roomId }),
        ...(shouldClearRoom && { roomId: null }),
      };
      const changedKeys = Object.keys(partialUpdate) as Array<keyof EventTiny>;
      const rangeEventSnapshots = new Map<
        readonly unknown[],
        OptimisticEventSnapshot<EventTiny>
      >();

      for (const [queryKey, events] of allRangeQueries) {
        const previous = events?.find((event) => event.id === eventId);
        if (!events || !previous) continue;
        const optimistic = { ...previous, ...partialUpdate };
        rangeEventSnapshots.set(queryKey, {
          previous,
          optimistic,
          changedKeys,
        });
        queryClient.setQueryData<EventTiny[]>(
          queryKey,
          events.map((event) => (event.id === eventId ? optimistic : event)),
        );
      }

      let tinySnapshot: OptimisticEventSnapshot<EventTiny> | undefined;
      if (tinyPrevious && changedKeys.length > 0) {
        const optimistic = { ...tinyPrevious, ...partialUpdate };
        tinySnapshot = { previous: tinyPrevious, optimistic, changedKeys };
        queryClient.setQueryData(eventQueryKeys.tiny(eventId), optimistic);
      }

      let detailSnapshot: OptimisticEventSnapshot<EventDetail> | undefined;
      if (detailPrevious) {
        const nextSegments = shiftSegmentsByEventStartDelta(
          detailPrevious.segments,
          detailPrevious.start,
          updates.start,
        );
        const optimistic: EventDetail = {
          ...detailPrevious,
          ...partialUpdate,
          segments: nextSegments as typeof detailPrevious.segments,
        };
        const detailChangedKeys = [
          ...(changedKeys as Array<keyof EventDetail>),
          ...(nextSegments !== detailPrevious.segments
            ? (["segments"] as Array<keyof EventDetail>)
            : []),
        ];
        if (detailChangedKeys.length > 0) {
          detailSnapshot = {
            previous: detailPrevious,
            optimistic,
            changedKeys: detailChangedKeys,
          };
          queryClient.setQueryData(eventQueryKeys.detail(eventId), optimistic);
        }
      }

      return {
        rangeEventSnapshots,
        tinySnapshot,
        detailSnapshot,
        originalEvent,
      };
    },

    onError: async (error, variables, context) => {
      const { id: eventId } = variables.updates;

      // Roll back only the event objects this mutation installed. Preserve
      // unrelated WebSocket and mutation changes that landed while it ran.
      for (const [queryKey, snapshot] of Array.from(
        context?.rangeEventSnapshots?.entries() ?? [],
      )) {
        queryClient.setQueryData<EventTiny[]>(queryKey, (events) =>
          events?.map((event) =>
            event.id === eventId
              ? rollbackOptimisticFields(event, snapshot)
              : event,
          ),
        );
      }
      if (context?.tinySnapshot) {
        queryClient.setQueryData<EventTiny>(
          eventQueryKeys.tiny(eventId),
          (current) =>
            current
              ? rollbackOptimisticFields(current, context.tinySnapshot!)
              : current,
        );
      }
      if (context?.detailSnapshot) {
        queryClient.setQueryData<EventDetail>(
          eventQueryKeys.detail(eventId),
          (current) =>
            current
              ? rollbackOptimisticFields(current, context.detailSnapshot!)
              : current,
        );
      }

      const errorMessage = await parseError(error);
      toast({
        title: "Error updating event",
        description:
          typeof errorMessage === "string"
            ? errorMessage
            : "Failed to update event.",
        variant: "error",
      });
    },

    onSuccess: async (response, variables, context) => {
      if (!response) return;
      const result = response.events;

      const { id: originalEventId, recurrenceModificationMode } =
        variables.updates;
      const isRecurringUpdate =
        recurrenceModificationMode &&
        [
          RecurrenceModificationMode.ALL,
          RecurrenceModificationMode.FUTURE,
        ].includes(recurrenceModificationMode);

      // For recurring updates, we need to remove old events and add new ones
      if (isRecurringUpdate) {
        await cancelMatchingQueries([
          { queryKey: eventQueryKeys.byLocations() },
          { queryKey: eventQueryKeys.lists() },
        ]);
        await Promise.all([
          queryClient.cancelQueries({
            queryKey: eventQueryKeys.tiny(originalEventId),
            predicate: ({ state }) => state.data !== undefined,
          }),
          queryClient.cancelQueries({
            queryKey: eventQueryKeys.detail(originalEventId),
            predicate: ({ state }) => state.data !== undefined,
          }),
        ]);

        // The backend creates a NEW recurring event with a new ID, so we must
        // use the OLD recurring event ID to identify series members.
        // Best source: variables.updates.recurringEvent?.id (the ID the user
        // sent to the backend). Fallbacks: cached event fields.
        const oldRecurringEventId =
          variables.updates.recurringEvent?.id ??
          context?.originalEvent?.recurringEventId ??
          context?.originalEvent?.recurringEvent?.id;
        const originalEventStart =
          context?.originalEvent?.start ??
          variables.updates.start ??
          new Date(0);
        replaceRecurringEventsInCaches(
          result,
          originalEventId,
          oldRecurringEventId,
          recurrenceModificationMode ?? RecurrenceModificationMode.ALL,
          originalEventStart,
        );
      } else {
        const cachePositions = [context?.originalEvent, ...result].filter(
          (event): event is EventTiny => !!event,
        );
        await cancelEventQueriesForEvents(cachePositions);
        const recurrenceWasAdded =
          !context?.originalEvent?.recurringEventId &&
          result.some((event) => event.recurringEventId);
        for (const event of result) {
          if (recurrenceWasAdded && event.id !== originalEventId) {
            addEventToCalendarCache(event);
          } else {
            updateEventInCalendarCaches(event);
          }
        }
        reconcileEventsInAllListCaches(result);
      }
      applyShiftBreakChanges(
        response.breakEvents,
        response.removedBreakEventIds,
      );

      // A THIS-scope edit on an existing recurring event changes one event.
      // Older APIs also returned its unchanged siblings in `result`, so count
      // it as one rather than toasting "N events updated".
      const wasAlreadyRecurring = !!context?.originalEvent?.recurringEventId;
      const updatedCount = isRecurringUpdate
        ? result.length
        : wasAlreadyRecurring
          ? 1
          : result.length;
      const message =
        updatedCount > 1
          ? `${updatedCount} events updated successfully!`
          : "Event updated successfully!";

      // Include events on both sides of the update so patient reassignment
      // cannot leave either checkout with a fresh stale result.
      invalidateTodaysItemsForEventPatients([
        ...(context?.originalEvent ? [context.originalEvent] : []),
        ...result,
      ]);

      toast({
        title: "Success",
        description: message,
        variant: "success",
      });

      if (response.googleCalendarFailures.length) {
        showGoogleCalendarWarning(response.googleCalendarFailures, "updated");
      }
    },

    onSettled: async (response, __, variables, context) => {
      const { id: eventId, attendeeIds } = variables.updates;

      // For recurring updates, invalidate detail for all affected events
      const eventIds = response?.events.map((e) => e.id) ?? [eventId];
      for (const id of eventIds) {
        queryClient.invalidateQueries({
          queryKey: eventQueryKeys.detail(id),
        });
        queryClient.invalidateQueries({
          queryKey: eventQueryKeys.appointmentChecklist(id),
        });
        queryClient.invalidateQueries(eventLogQueryOptions.list(id));
      }

      // Invalidate patient events for all attendees
      for (const patientId of attendeeIds || []) {
        queryClient.invalidateQueries({
          queryKey: eventQueryKeys.searchPatientEvents(patientId, ""),
        });
      }

      if (response && eventUpdateAffectsShiftConflicts(variables.updates)) {
        const eventType =
          response.events[0]?.type ??
          context?.originalEvent?.type ??
          variables.updates.type;
        await invalidateShiftConflictsForEventType(eventType);
      }
    },
  });
};

export function updateGeneratedGoogleMeetLinkInCaches(
  event: EventDetail,
): void {
  const mergeEvent = <T extends EventTiny>(cachedEvent: T): T =>
    ({
      ...cachedEvent,
      ...event,
      // These fields are derived by the read endpoints and are not populated
      // by the meeting-link endpoint. Generating a link cannot change them.
      carePlan: cachedEvent.carePlan,
      noteIds: cachedEvent.noteIds,
      icons: cachedEvent.icons,
    }) as T;
  const updateEvents = <T extends EventTiny>(events: T[] | undefined) =>
    events?.map((cachedEvent) =>
      cachedEvent.id === event.id ? mergeEvent(cachedEvent) : cachedEvent,
    );

  queryClient.setQueryData<EventTiny>(
    eventQueryKeys.tiny(event.id),
    (cachedEvent) => (cachedEvent ? mergeEvent(cachedEvent) : undefined),
  );
  queryClient.setQueriesData<EventTiny[]>(
    { queryKey: eventQueryKeys.byLocations(), exact: false },
    updateEvents,
  );
  queryClient.setQueriesData<EventTiny[]>(
    { queryKey: eventQueryKeys.lists(), exact: false },
    updateEvents,
  );
  queryClient.setQueryData<EventDetail>(
    eventQueryKeys.detail(event.id),
    (cachedEvent) => (cachedEvent ? mergeEvent(cachedEvent) : undefined),
  );
  queryClient.setQueriesData<EventDetail[]>(
    { queryKey: eventQueryKeys.allPatientResults(), exact: false },
    updateEvents,
  );
}

export const useGenerateGoogleMeetLinkMutation = () => {
  return useMutation<EventDetail | null, Error, { eventId: string }>({
    mutationFn: async ({ eventId }) => generateGoogleMeetLink(eventId),
    onSuccess: (event) => {
      if (!event) return;
      updateGeneratedGoogleMeetLinkInCaches(event);
    },
  });
};

export const useResendEventConfirmationMutation = () => {
  return useMutation<{ sent: boolean } | null, Error, { eventId: string }>({
    mutationFn: async ({ eventId }) => resendEventConfirmation(eventId),
    onSuccess: (_data, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.eventMessages(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.messageTypes(eventId),
      });
      toast({ title: "Confirmation resent", variant: "success" });
    },
    onError: async (error) => {
      toast({
        title: "Failed to resend confirmation",
        description: await parseError(error),
        variant: "error",
      });
    },
  });
};

/**
 * Mutation hook for authorizing Google Calendar for the current user.
 * Returns an authorization URL that should be opened in a new window.
 */
export const useAuthorizeGoogleCalendarMutation = ({
  showErrorToast = true,
}: { showErrorToast?: boolean } = {}) => {
  return useMutation<string | null, Error, void>({
    mutationFn: async () => authorizeGoogleCalendar(),
    onError: showErrorToast
      ? () => {
          toast({
            title: "Connection Failed",
            description:
              "Failed to connect to Google Calendar. Please try again.",
            variant: "error",
          });
        }
      : undefined,
  });
};

/**
 * Mutation hook for authorizing Google Calendar at the tenant level.
 * Returns an authorization URL that should be opened in a new window.
 */
export const useAuthorizeTenantGoogleCalendarMutation = ({
  showErrorToast = true,
}: { showErrorToast?: boolean } = {}) => {
  return useMutation<string | null, Error, void>({
    mutationFn: async () => authorizeTenantGoogleCalendar(),
    onError: showErrorToast
      ? () => {
          toast({
            title: "Connection Failed",
            description:
              "Failed to connect to Google Calendar. Please try again.",
            variant: "error",
          });
        }
      : undefined,
  });
};

export const useDeleteEventMutation = () => {
  const showGoogleCalendarWarning = useGoogleCalendarPartialSuccessToast();

  return useMutation<
    EventDeleteResponse | null,
    Error,
    {
      eventId: string;
      eventType: EventType;
      eventStart: Date;
      recurrenceModificationMode?: RecurrenceModificationMode;
      cancellationReason?: string;
      sendSms?: boolean;
      cancellationMessage?: string;
    },
    {
      rangeQueriesPrevious?: Map<readonly unknown[], EventTiny[] | undefined>;
      tinyPrevious?: EventTiny;
    }
  >({
    mutationFn: async ({
      eventId,
      recurrenceModificationMode,
      cancellationReason,
      sendSms = true,
      cancellationMessage,
    }) => {
      if (
        recurrenceModificationMode &&
        [
          RecurrenceModificationMode.ALL,
          RecurrenceModificationMode.FUTURE,
        ].includes(recurrenceModificationMode)
      ) {
        return deleteRecurringEvent(eventId, recurrenceModificationMode);
      } else {
        return deleteEvent(
          eventId,
          recurrenceModificationMode || RecurrenceModificationMode.THIS,
          cancellationReason,
          sendSms,
          cancellationMessage,
        );
      }
    },

    onMutate: async ({ eventId, eventType, recurrenceModificationMode }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: eventQueryKeys.byLocations(),
      });
      await queryClient.cancelQueries({
        queryKey: eventQueryKeys.tiny(eventId),
      });

      // Capture previous states for rollback
      const rangeQueriesPrevious = new Map<
        readonly unknown[],
        EventTiny[] | undefined
      >();
      const allRangeQueries = queryClient.getQueriesData<EventTiny[]>({
        queryKey: eventQueryKeys.byLocations(),
      });
      for (const [queryKey, data] of allRangeQueries) {
        rangeQueriesPrevious.set(queryKey, data);
      }

      const tinyPrevious = queryClient.getQueryData<EventTiny>(
        eventQueryKeys.tiny(eventId),
      );

      // For recurring event deletions (FUTURE/ALL), we can't know which events
      // will be deleted until the server responds. Skip optimistic updates for these.
      const isRecurringDelete =
        recurrenceModificationMode &&
        [
          RecurrenceModificationMode.ALL,
          RecurrenceModificationMode.FUTURE,
        ].includes(recurrenceModificationMode);

      if (!isRecurringDelete) {
        // Optimistically update based on event type (single event deletion)
        if (eventType === EventType.BLOCK || eventType === EventType.SHIFT) {
          // For blocks and shifts, remove from all calendar caches
          removeEventFromCalendarCaches(eventId);
        } else {
          // For appointments, update status to CANCELLED in all calendar caches
          updateEventStatusInCalendarCaches(eventId, EventStatus.CANCELLED);
        }
      }

      return { rangeQueriesPrevious, tinyPrevious };
    },

    onError: async (error, variables, context) => {
      // Revert caches
      if (context?.rangeQueriesPrevious) {
        Array.from(context.rangeQueriesPrevious.entries()).forEach(
          ([queryKey, data]) => {
            if (data !== undefined) {
              queryClient.setQueryData(queryKey, data);
            }
          },
        );
      }
      if (context?.tinyPrevious) {
        queryClient.setQueryData(
          eventQueryKeys.tiny(variables.eventId),
          context.tinyPrevious,
        );
      }

      const errorMessage = await parseError(error);
      toast({
        title: "Error deleting event",
        description:
          typeof errorMessage === "string"
            ? errorMessage
            : "Failed to delete event.",
        variant: "error",
      });
    },

    onSuccess: (response, variables) => {
      applyShiftBreakChanges([], response?.removedBreakEventIds);
      const deletedEventIds = response?.eventIds;
      if (!deletedEventIds || deletedEventIds.length === 0) return;

      const { eventType, recurrenceModificationMode } = variables;
      const deletedIdSet = new Set(deletedEventIds);
      const isRecurringDelete =
        recurrenceModificationMode &&
        [
          RecurrenceModificationMode.ALL,
          RecurrenceModificationMode.FUTURE,
        ].includes(recurrenceModificationMode);

      // For recurring deletes, update all affected events in all caches
      if (isRecurringDelete) {
        if (eventType === EventType.BLOCK || eventType === EventType.SHIFT) {
          // For blocks and shifts, remove all deleted events from all caches
          for (const id of deletedEventIds) {
            removeEventFromCalendarCaches(id);
          }
        } else {
          // For appointments, mark all deleted events as CANCELLED in all caches
          for (const id of deletedEventIds) {
            updateEventStatusInCalendarCaches(id, EventStatus.CANCELLED);
          }
        }
      }

      // Also update all list caches (e.g., shift management page)
      if (eventType === EventType.BLOCK || eventType === EventType.SHIFT) {
        // For blocks and shifts, remove from all list caches
        removeEventsFromAllListCaches(deletedIdSet);
      }
      // For appointments, list caches will show CANCELLED status from range cache update

      const message =
        deletedEventIds.length > 1
          ? `${deletedEventIds.length} events deleted successfully.`
          : "Event deleted successfully.";

      toast({
        title: "Success",
        description: message,
        variant: "success",
      });

      if (response.googleCalendarFailures.length) {
        showGoogleCalendarWarning(response.googleCalendarFailures, "cancelled");
      }
    },

    onSettled: async (response, __, variables) => {
      const { eventId } = variables;

      // Invalidate detail cache for all deleted events
      const idsToInvalidate = response?.eventIds ?? [eventId];
      for (const id of idsToInvalidate) {
        queryClient.invalidateQueries({
          queryKey: eventQueryKeys.detail(id),
        });
        queryClient.invalidateQueries(eventLogQueryOptions.list(id));
      }

      if (response?.eventIds.length) {
        await invalidateShiftConflictsForEventType(variables.eventType);
      }
    },
  });
};
